import {Component, OnInit, Input} from "@angular/core";
import {FormGroup, FormControl} from "@angular/forms";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ExamService} from "../../../shared/services/exam/exam.service";
import {take} from "rxjs/operators";
import {Exam} from "../../../shared/models/exam";
import {ShortExamSession} from "src/app/shared/models/examSession";
import {Certification} from "src/app/shared/models/certification";
import {ExamSessionService} from "src/app/shared/services/exam-session/exam-session.service";
import {CertificationService} from "src/app/shared/services/certification/certification.service";
import {forkJoin} from "rxjs";

@Component({
    selector: "app-exam-edition-modal",
    templateUrl: "./exam-edition-modal.component.html",
    styleUrls: ["./exam-edition-modal.component.scss"]
})
export class ExamEditionModalComponent implements OnInit {
    @Input() exam: Exam;

    // Whether to expose the candidate professional-situation fields. Set by the
    // opener (exam-list) to staff / exam center boss only.
    @Input() canEditProfessional: boolean = false;

    examForm: FormGroup;
    isDataLoading: boolean = true;
    error: string;
    submitting: boolean = false;
    validationErrors: Record<string, string>;
    sessions: Array<ShortExamSession>;
    certifications: Array<Certification>;

    constructor (
        public activeModal: NgbActiveModal,
        public examService: ExamService,
        public examSessionService: ExamSessionService,
        public certificationService: CertificationService
    ) {}

    ngOnInit () {
        // Load data

        const getSessions = this.examSessionService.shortList(
            this.exam.exam_center.id
        );
        const getCertifications = this.certificationService.shortListUnarchived();

        forkJoin([
            getSessions,
            getCertifications
        ]).pipe(take(1)).
            subscribe(([
                sessions,
                certifications
            ]) => {
                this.sessions = sessions;
                this.certifications = certifications;
                this.resetForm();
                this.isDataLoading = false;
            });

    }

    private resetForm () {
        this.examForm = new FormGroup({
            fileNumber: new FormControl(this.exam.file_number),
            certificationId: new FormControl(this.exam.certification
                ? this.exam.certification.id
                : null),
            sessionId: new FormControl(this.exam.session
                ? this.exam.session.id
                : null)
        });

        // Candidate professional situation (stored per-voucher). Editable only
        // by staff / exam center boss; reuses the signup autocompletion
        // components. Other roles never get these controls, so they are never
        // sent (the backend rejects them for those roles anyway).
        if (this.canEditProfessional) {
            this.examForm.addControl("qualification_level", new FormControl(this.exam.qualification_level));
            this.examForm.addControl("profession", new FormControl(this.exam.profession));
            this.examForm.addControl("objective", new FormControl(this.exam.objective));
        }
    }

    validate () {
        this.submitting = true;

        const data: Record<string, unknown> = {
            // eslint-disable-next-line camelcase
            file_number: this.examForm.value.fileNumber,

            // eslint-disable-next-line camelcase
            certification_id: this.examForm.value.certificationId,

            // eslint-disable-next-line camelcase
            session_id: this.examForm.value.sessionId
        };

        // Professional situation: only included for staff / exam center boss.
        if (this.canEditProfessional) {
            // eslint-disable-next-line camelcase
            data.qualification_level = this.examForm.value.qualification_level;
            data.profession = this.examForm.value.profession;
            data.objective = this.examForm.value.objective;
        }

        this.examService.partialUpdate(
            this.exam.id,
            data
        ).pipe(take(1)).
            subscribe(
                (res: Exam) => {
                    this.activeModal.close(res);
                },
                (err) => {
                    if (err.status === 400) {
                        this.validationErrors = err.error;
                    } else {
                        this.error = "Une erreur est survenue lors de la création des codes";
                        throw err;
                    }
                    this.submitting = false;
                }
            );
    }

}
